import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import UserAvatarSVG from '@/components/UserAvatarSVG';
import '@/styles/FootfallDashStyles.scss';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

const FootFallDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [monthlyFootfallData, setMonthlyFootfallData] = useState([]);
    const [monthlyEntranceData, setMonthlyEntranceData] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setInitialLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    const fetchMonthlyFootfallData = async () => {
        try {
            const response = await apiFetch('/api/RetailDynamics/monthly-totals-previous-year');
            
            // Check if the response is ok before parsing
            if (!response.ok) {
                // For non-2xx responses (except 401 which is handled by apiFetch),
                // log the error and show user-friendly toast notification
                console.warn(`FootFall API returned status ${response.status} for monthly-totals-previous-year`);
                toast.warning('Unable to load FootFall data. The FootFall service may be temporarily unavailable.', {
                    autoClose: 8000,
                });
                return;
            }
            
            const data = await response.json();
            if (data) {
                setMonthlyFootfallData(data);
            }
        } catch (e) {
            Sentry.captureException(e);
            // If the error is a session expiry, let it propagate
            // Otherwise, log and show toast notification
            if (e.message && e.message.includes('Session expired')) {
                throw e; // Re-throw session expiry errors to trigger logout
            }
            console.error('Error fetching monthly footfall data:', e);
            toast.warning('Unable to load FootFall data. Please try refreshing the page.', {
                autoClose: 8000,
            });
            // Dashboard continues to work, just without this data
        }
    }

    const fetchFootFallEntranceData = async () => {
        try {
            const response = await apiFetch('/api/RetailDynamics/monthly-totals-by-entrance');
            
            // Check if the response is ok before parsing
            if (!response.ok) {
                // For non-2xx responses (except 401 which is handled by apiFetch),
                // log the error and show user-friendly toast notification
                console.warn(`FootFall API returned status ${response.status} for monthly-totals-by-entrance`);
                toast.warning('Unable to load FootFall entrance data. The FootFall service may be temporarily unavailable.', {
                    autoClose: 8000,
                });
                return;
            }
            
            const data = await response.json();
            if (data) {
                setMonthlyEntranceData(data);
            }
        } catch (e) {
            Sentry.captureException(e);
            // If the error is a session expiry, let it propagate
            // Otherwise, log and show toast notification
            if (e.message && e.message.includes('Session expired')) {
                throw e; // Re-throw session expiry errors to trigger logout
            }
            console.error('Error fetching entrance data:', e);
            toast.warning('Unable to load FootFall entrance data. Please try refreshing the page.', {
                autoClose: 8000,
            });
            // Dashboard continues to work, just without this data
        }
    }

    useEffect(() => {
        fetchMonthlyFootfallData();
        fetchFootFallEntranceData();
    }, []);

    function getPreviousMonthYear() {
        const now = new Date();
        const month = now.getMonth() - 1; // 0-based
        const year = now.getFullYear();

        // Current month/year
        const current = new Date(year, month, 1);
        // Previous year, same month
        const previous = new Date(year - 1, month, 1);

        const options = { month: 'long', year: 'numeric' };

        return {
            current: current.toLocaleString('default', options),
            previous: previous.toLocaleString('default', options)
        };
    }

    // Utility: get all unique mall names, sorted alphabetically
    const uniqueMallNames = [...new Set(monthlyFootfallData.map(d => d.mall_name))].sort();

    // Month names for chart x-axis
    const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const fullMonthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    // Returns an array of { month, year } objects for the last 12 months
    function getLast12Months(currentYear, currentMonth) {
        const months = [];
        for (let i = 11; i >= 0; i--) {
            let year = currentYear;
            let month = currentMonth - i;
            if (month <= 0) {
                month += 12;
                year -= 1;
            }
            months.push({ year, month });
        }
        return months;
    }

    // Prepares chart data for a given mall
    function getMallChartData(mallName, monthlyFootfallData, currentYear, currentMonth, monthNames) {
        const months = getLast12Months(currentYear, currentMonth);
        return months.map(({ year, month }) => {
            const current = monthlyFootfallData.find(
                d => d.mall_name === mallName && d.year === year && d.month === month
            );
            const prior = monthlyFootfallData.find(
                d => d.mall_name === mallName && d.year === (year - 1) && d.month === month
            );
            return {
                name: monthNames[month - 1],
                current: current ? current.foot_count : null,
                prior: prior ? prior.foot_count : null
            };
        }).filter(d => d.current !== null || d.prior !== null); // Show if at least one value exists
    }

    // Utility functions for summary table (unchanged)
    function getFootCount(monthlyFootfallData, mallName, year, month) {
        const record = monthlyFootfallData.find(
            d => d.mall_name === mallName && d.year === year && d.month === month
        );
        const value = record ? record.foot_count : 0;
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }

    function getTotalFootCount(monthlyFootfallData, year, month) {
        // Sum foot_count for all malls for the given year and month
        const total = uniqueMallNames.reduce((sum, mallName) => {
            const record = monthlyFootfallData.find(
                d => d.mall_name === mallName && d.year === year && d.month === month
            );
            return sum + (record ? record.foot_count : 0);
        }, 0);
        return total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    function getFootCountRaw(monthlyFootfallData, mallName, year, month) {
        const record = monthlyFootfallData.find(
            d => d.mall_name === mallName && d.year === year && d.month === month
        );
        return record ? record.foot_count : 0;
    }

    function getTotalFootCountRaw(monthlyFootfallData, year, month) {
        return uniqueMallNames.reduce((sum, mallName) => {
            const record = monthlyFootfallData.find(
                d => d.mall_name === mallName && d.year === year && d.month === month
            );
            return sum + (record ? record.foot_count : 0);
        }, 0);
    }

    function getVariance(current, previous) {
        if (previous === 0) return { percent: 0, direction: 0 }; // avoid division by zero
        const diff = current - previous;
        const percent = ((diff / previous) * 100).toFixed(1);
        let direction = 0;
        if (diff > 0) direction = 1;
        else if (diff < 0) direction = -1;
        return { percent, direction };
    }

    function MovementCell({ current, previous }) {
        const { percent, direction } = getVariance(current, previous);

        // SVG arrows for thickness
        const upArrow = (
            <svg
                width="14"
                height="14"
                style={{
                    verticalAlign: 'middle',
                    marginRight: 4,
                    display: 'inline-block'
                }}
            >
                <polyline points="7,12 7,2" stroke="green" strokeWidth="3" fill="none" />
                <polyline points="3,6 7,2 11,6" stroke="green" strokeWidth="3" fill="none" />
            </svg>
        );
        const downArrow = (
            <svg
                width="14"
                height="14"
                style={{
                    verticalAlign: 'middle',
                    marginRight: 4,
                    display: 'inline-block'
                }}
            >
                <polyline points="7,2 7,12" stroke="red" strokeWidth="3" fill="none" />
                <polyline points="3,8 7,12 11,8" stroke="red" strokeWidth="3" fill="none" />
            </svg>
        );

        let arrow = null;
        if (direction === 1) arrow = upArrow;
        else if (direction === -1) arrow = downArrow;

        return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {arrow}
                {Math.abs(percent)}%
            </span>
        );
    };

    // Gets the name of the entrance with the highest footcount 
    function getBusiestEntrance(monthlyEntranceData, mallName, year, month) {
        const entrances = monthlyEntranceData.filter(
            d => d.mall_name === mallName && d.year === year && d.month === month
        );
        if (entrances.length === 0) return null;
        const busiest = entrances.reduce((max, curr) =>
            curr.foot_count > max.foot_count ? curr : max
        );
        return busiest.entrance_name;
    };

    // Gets the count for the busiest entrance for each mall
    function getBusiestEntranceFootCount(monthlyEntranceData, mallName, year, month) {
        const entrances = monthlyEntranceData.filter(
            d => d.mall_name === mallName && d.year === year && d.month === month
        );
        if (entrances.length === 0) return "0";
        const busiest = entrances.reduce((max, curr) =>
            curr.foot_count > max.foot_count ? curr : max
        );
        return busiest.foot_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    // Gets the % of total monthly footfall through the busiest entrance
    function getBusiestEntrancePercentage(monthlyEntranceData, monthlyFootfallData, mallName, year, month) {
        const entrances = monthlyEntranceData.filter(
            d => d.mall_name === mallName && d.year === year && d.month === month
        );
        if (entrances.length === 0) return "0%";
        const busiest = entrances.reduce((max, curr) =>
            curr.foot_count > max.foot_count ? curr : max
        );
        const busiestCount = busiest.foot_count;
        const mallRecord = monthlyFootfallData.find(
            d => d.mall_name === mallName && d.year === year && d.month === month
        );
        const totalCount = mallRecord ? mallRecord.foot_count : 0;
        if (!totalCount || totalCount === 0) return "0%";
        const percent = ((busiestCount / totalCount) * 100).toFixed(1);
        return `${percent}%`;
    };

    // Gets average of % of total monthly footfall through busiest entrance
    function getAverageBusiestEntrancePercentage(monthlyEntranceData, monthlyFootfallData, mallNames, year, month) {
        let totalPercent = 0;
        let count = 0;
        mallNames.forEach(mallName => {
            const percentStr = getBusiestEntrancePercentage(
                monthlyEntranceData,
                monthlyFootfallData,
                mallName,
                year,
                month
            );
            const percent = parseFloat(percentStr.replace('%', ''));
            if (!isNaN(percent)) {
                totalPercent += percent;
                count++;
            }
        });
        if (count === 0) return "0%";
        const avg = (totalPercent / count).toFixed(1);
        return `${avg}%`;
    }

    const { current, previous } = getPreviousMonthYear();

    const now = new Date();
    let currentYear = now.getFullYear();
    let currentMonth = now.getMonth(); // 0-based, so January is 0

    if (currentMonth === 0) {
        currentMonth = 12;
        currentYear -= 1;
    }

    const previousYear = currentYear - 1;
    const previousMonth = currentMonth;

    // Prepare summary table data for all malls
    const summaryRows = uniqueMallNames.map(mallName => {
        const prevMonth = getFootCount(monthlyFootfallData, mallName, currentYear, currentMonth);
        const prevMonthPrevYear = getFootCount(monthlyFootfallData, mallName, previousYear, previousMonth);
        const currentRaw = getFootCountRaw(monthlyFootfallData, mallName, currentYear, currentMonth);
        const previousRaw = getFootCountRaw(monthlyFootfallData, mallName, previousYear, previousMonth);
        const busiestEntrance = getBusiestEntrance(monthlyEntranceData, mallName, currentYear, currentMonth);
        const busiestFootCount = getBusiestEntranceFootCount(monthlyEntranceData, mallName, currentYear, currentMonth);
        const busiestPercent = getBusiestEntrancePercentage(monthlyEntranceData, monthlyFootfallData, mallName, currentYear, currentMonth);
        return {
            mallName,
            prevMonth,
            prevMonthPrevYear,
            currentRaw,
            previousRaw,
            busiestEntrance,
            busiestFootCount,
            busiestPercent
        };
    });

    const totalCurrent = getTotalFootCount(monthlyFootfallData, currentYear, currentMonth);
    const totalPrevious = getTotalFootCount(monthlyFootfallData, previousYear, previousMonth);
    const avgBusiestPercent = getAverageBusiestEntrancePercentage(
        monthlyEntranceData,
        monthlyFootfallData,
        uniqueMallNames,
        currentYear,
        currentMonth
    );

    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                <div>
                    <div className="dashboard-greeting">
                        <UserAvatarSVG />
                        Hello {user.FirstName || user.firstName}
                    </div>
                    <h3>Footfall Dashboard</h3>
                    <div>
                        <div style={{ margin: "0 auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "left", padding: "8px" }}>Footfall Summary</th>
                                        <th style={{ textAlign: "right", padding: "8px" }}>Total Footfall <br />{current}</th>
                                        <th style={{ textAlign: "right", padding: "8px" }}>Total Footfall <br />{previous}</th>
                                        <th style={{ textAlign: "right", padding: "8px" }}>% Movement</th>
                                        <th style={{ textAlign: "right", padding: "8px" }}>Busiest Entrance by Footfall</th>
                                        <th style={{ textAlign: "right", padding: "8px" }}>Number of Feet <br />Through Busiest Entrance</th>
                                        <th style={{ textAlign: "right", padding: "8px" }}>% of Total Monthly Footfall <br />through Busiest Entrance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryRows.map(row => (
                                        <tr key={row.mallName}>
                                            <td>{row.mallName}</td>
                                            <td>{row.prevMonth}</td>
                                            <td>{row.prevMonthPrevYear}</td>
                                            <td>
                                                <MovementCell
                                                    current={row.currentRaw}
                                                    previous={row.previousRaw}
                                                />
                                            </td>
                                            <td style={{ textAlign: "right" }}>{row.busiestEntrance}</td>
                                            <td>{row.busiestFootCount}</td>
                                            <td>{row.busiestPercent}</td>
                                        </tr>
                                    ))}
                                    <tr className="dark-gray">
                                        <td>Total</td>
                                        <td>{totalCurrent}</td>
                                        <td>{totalPrevious}</td>
                                        <td>
                                            <MovementCell
                                                current={getTotalFootCountRaw(monthlyFootfallData, currentYear, currentMonth)}
                                                previous={getTotalFootCountRaw(monthlyFootfallData, previousYear, previousMonth)}
                                            />
                                        </td>
                                        <td style={{ textAlign: "center" }}></td>
                                        <td></td>
                                        <td>{avgBusiestPercent}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div>
                            {uniqueMallNames.map(mallName => {
                                const chartData = getMallChartData(mallName, monthlyFootfallData, currentYear, currentMonth, monthNames);
                                const last = chartData.length > 0 ? chartData[chartData.length - 1] : null;
                                return (
                                    <div className="bridge-graph" key={mallName}>
                                        <div className="graph-heading">{mallName}</div>
                                        <div>
                                            {chartData.length > 0 && last && (
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <LineChart data={chartData} margin={{ top: 20, right: 40, left: 60, bottom: 20 }}>
                                                        <CartesianGrid stroke="#000" vertical={false} />
                                                        <XAxis dataKey="name" />
                                                        <YAxis
                                                            tickFormatter={v => v?.toLocaleString('en-US').replace(/,/g, '\u00A0')}
                                                            domain={['auto', 'auto']}
                                                            label={{ value: 'Footfall', angle: -90, position: 'insideLeft', offset: -20 }}
                                                        />
                                                        <Tooltip
                                                            formatter={v => v?.toLocaleString('en-US').replace(/,/g, '\u00A0')}
                                                            labelFormatter={label => {
                                                                const idx = monthNames.indexOf(label);
                                                                return idx !== -1 ? fullMonthNames[idx] : label;
                                                            }}
                                                        />
                                                        <Legend verticalAlign="top" height={36} />
                                                        <Line type="monotone" dataKey="current" name="Current Footfall" stroke="#d32f2f" strokeWidth={2} dot={false} />
                                                        <Line type="monotone" dataKey="prior" name="Prior Footfall" stroke="#222" strokeWidth={2} dot={false} />
                                                        {/* End value labels */}
                                                        <text x="98%" y={60} textAnchor="end" fill="#d32f2f" fontSize={13}>
                                                            {last.current?.toLocaleString('en-US').replace(/,/g, ' ') ?? ''}
                                                        </text>
                                                        <text x="98%" y={80} textAnchor="end" fill="#222" fontSize={13}>
                                                            {last.prior?.toLocaleString('en-US').replace(/,/g, ' ') ?? ''}
                                                        </text>
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default FootFallDashboard;